import {
  DgaButton as Button,
  DgaTextarea as Textarea,
  DgaCheckbox,
  DgaRadioButton,
  DgaIcon,
  DgaLink,
  DgaNotification
} from "platformscode-new-react";

import React, { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { getFeedbackStats, saveFeedback } from "../../services/api";

// نظام Logging مفصل للـ Feedback
const FeedbackLogger = {
  log: (level, action, data = {}) => {
    const timestamp = new Date().toISOString();
    const logEntry = {
      timestamp,
      level,
      action,
      component: "Feedback",
      url: window.location.href,
      userAgent: navigator.userAgent,
      ...data,
    };

    // طباعة في Console
    // console.log(`[FEEDBACK-${level.toUpperCase()}] ${action}:`, logEntry);

    // حفظ في localStorage للمراجعة اللاحقة
    try {
      const existingLogs = JSON.parse(
        localStorage.getItem("feedback_logs") || "[]"
      );
      existingLogs.push(logEntry);

      // الاحتفاظ بآخر 100 سجل فقط
      if (existingLogs.length > 100) {
        existingLogs.splice(0, existingLogs.length - 100);
      }

      localStorage.setItem("feedback_logs", JSON.stringify(existingLogs));
    } catch (e) {
      console.warn("فشل في حفظ اللوق:", e);
    }

    return logEntry;
  },

  info: (action, data) => FeedbackLogger.log("info", action, data),
  warn: (action, data) => FeedbackLogger.log("warn", action, data),
  error: (action, data) => FeedbackLogger.log("error", action, data),

  // دالة لتصدير جميع اللوقات
  exportLogs: () => {
    try {
      const logs = JSON.parse(localStorage.getItem("feedback_logs") || "[]");
      const dataStr = JSON.stringify(logs, null, 2);
      const dataBlob = new Blob([dataStr], { type: "application/json" });
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `feedback_logs_${new Date().toISOString().split("T")[0]
        }.json`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error("فشل في تصدير اللوقات:", e);
    }
  },

  // دالة لمسح اللوقات
  clearLogs: () => {
    localStorage.removeItem("feedback_logs");
    console.log("تم مسح جميع لوقات الفيدباك");
  },
};

const Feedback = ({ feedbackOptions }) => {
  const location = useLocation();
  const textareaRef = useRef(null);

  // حالات الفيدباك فقط (تم حذف حالات التقييم)
  const [answer, setAnswer] = useState({
    isUseful: null,
    reasons: [],
    notes: "",
    gender: "",
  });

  const [errors, setErrors] = useState({
    reasons: false,
    gender: false,
  });

  const [openQuestions, setOpenQuestions] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [options, setOptions] = useState({
    yes: [],
    no: [],
  });

  const [stats, setStats] = useState({
    yesPercentage: 0,
    totalCount: 0,
  });

  // دالة للحصول على اسم الصفحة
  const getPageName = () => {
    return window.location.pathname.substring(1).split("?")[0] || "home";
  };

  // جلب إحصائيات الفيدباك مع Logging
  const fetchFeedbackStats = async () => {
    const pageUrl = getPageName();

    FeedbackLogger.info("بدء جلب إحصائيات الفيدباك", { pageUrl });

    try {
      const data = await getFeedbackStats(pageUrl);

      FeedbackLogger.info("تم تحليل رد إحصائيات الفيدباك", {
        data,
        success: data?.success,
      });

      if (data.data) {
        setStats({
          yesPercentage: data.data.yes_percentage || 0,
          totalCount: data.data.total_count || 0,
        });

        FeedbackLogger.info("تم تحديث إحصائيات الفيدباك", {
          yesPercentage: data.data.yes_percentage,
          totalCount: data.data.total_count,
        });
      }
    } catch (error) {
      FeedbackLogger.error("خطأ في جلب إحصائيات الفيدباك", {
        error: error.message,
        pageUrl,
      });
      console.error("خطأ في جلب إحصائيات التقييم:", error);
    }
  };

  useEffect(() => {
    FeedbackLogger.info("تحديث feedbackOptions", {
      feedbackOptions,
      hasOptions: !!feedbackOptions,
    });

    if (feedbackOptions) {
      setOptions(feedbackOptions);
    }
  }, [feedbackOptions]);

  useEffect(() => {
    FeedbackLogger.info("تغيير الصفحة - جلب الإحصائيات", {
      pathname: location.pathname,
      hash: location.hash,
    });

    fetchFeedbackStats();

    const statsInterval = setInterval(() => {
      FeedbackLogger.info("تحديث دوري للإحصائيات");
      fetchFeedbackStats();
    }, 5 * 60 * 1000);

    return () => {
      clearInterval(statsInterval);
    };
  }, [location.pathname]);

  useEffect(() => {
    if (textareaRef.current && textareaRef.current.shadowRoot) {
      const textareaElement =
        textareaRef.current.shadowRoot.querySelector("textarea");
      if (textareaElement) {
        textareaElement.addEventListener("input", (e) => {
          FeedbackLogger.info("تغيير نص الملاحظات", {
            value: e.target.value,
            length: e.target.value.length,
          });
          setAnswer((prev) => ({ ...prev, notes: e.target.value }));
        });
      }
    }
  }, [textareaRef.current]);

  const handleReasonChange = (reasonId) => {
    const newReasons = answer.reasons.includes(reasonId)
      ? answer.reasons.filter((r) => r !== reasonId)
      : [...answer.reasons, reasonId];

    FeedbackLogger.info("تغيير سبب الفيدباك", {
      reasonId,
      oldReasons: answer.reasons,
      newReasons,
      action: answer.reasons.includes(reasonId) ? "remove" : "add",
    });

    setAnswer({ ...answer, reasons: newReasons });
    if (newReasons.length > 0) {
      setErrors((prev) => ({ ...prev, reasons: false }));
    }
  };

  const handleGenderChange = (gender) => {
    FeedbackLogger.info("تغيير الجنس", {
      oldGender: answer.gender,
      newGender: gender,
    });

    setAnswer({ ...answer, gender });
    setErrors((prev) => ({ ...prev, gender: false }));
  };

  const validateForm = () => {
    const newErrors = {
      reasons: answer.reasons.length === 0,
      gender: !answer.gender,
    };

    FeedbackLogger.info("التحقق من صحة النموذج", {
      answer,
      errors: newErrors,
      isValid: !Object.values(newErrors).some((error) => error),
    });

    setErrors(newErrors);
    return !Object.values(newErrors).some((error) => error);
  };

  const handleSubmit = async () => {
    FeedbackLogger.info("بدء إرسال الفيدباك", {
      answer,
      textareaValue: textareaRef.current?.value,
    });

    if (!validateForm()) {
      FeedbackLogger.warn("فشل التحقق من صحة النموذج", {
        errors,
        answer,
      });
      return;
    }

    try {
      let notesValue = answer.notes;
      if (textareaRef.current && textareaRef.current.shadowRoot) {
        const textareaElement =
          textareaRef.current.shadowRoot.querySelector("textarea");
        if (textareaElement) {
          notesValue = textareaElement.value;
        }
      }

      const pageUrl = getPageName();
      const payload = {
        isUseful: answer.isUseful,
        pageUrl: pageUrl,
        reasons: answer.reasons,
        notes: notesValue,
        gender: answer.gender,
      };

      FeedbackLogger.info("إرسال طلب حفظ الفيدباك", { payload });

      const data = await saveFeedback(payload);

      FeedbackLogger.info("تم تحليل رد حفظ الفيدباك", {
        data,
        success: data?.success,
      });

      setSubmitted(true);
      fetchFeedbackStats();

      FeedbackLogger.info("تم حفظ الفيدباك بنجاح", {
        responseData: data,
      });
    } catch (error) {
      FeedbackLogger.error("خطأ في إرسال الفيدباك", {
        error: error.message,
        stack: error.stack,
      });
      console.error("Failed to submit feedback:", error);
      alert("حدث خطأ في الاتصال بالخادم");
    }
  };

  useEffect(() => {
    FeedbackLogger.info("إعادة تعيين الحالات عند تغيير الصفحة", {
      pathname: location.pathname,
    });

    // إعادة تعيين جميع الحالات عند تغيير الصفحة
    setSubmitted(false);
    setOpenQuestions(false);
    setAnswer({
      isUseful: null,
      reasons: [],
      notes: "",
      gender: "",
    });
    setErrors({
      reasons: false,
      gender: false,
    });
  }, [location.pathname]);

  return (
    <>
      <hr className="c-divider" />
      <div className="c-ontent">
        <section className="flex flex-col gap-8 cw-full !py-[24px]">
          {/* قسم "هل كانت هذه الصفحة مفيدة؟" فقط */}
          <div className="w-full flex flex-col md:flex-row md:justify-between md:items-center max-md:gap-4">
            <div className="flex md:items-center flex-col md:flex-row gap-4 md:gap-6">
              {submitted && (
                <DgaIcon
                  icon="checkmark-circle-04"
                  variant="stroke"
                  type="rounded"
                  color="#1B8354"
                  size="24px"
                />
              )}
              <p className="text-md-regular text-[#161616]">
                {!submitted
                  ? "هل كانت هذه الصفحة مفيدة؟"
                  : "تم إرسال ملاحظاتك!"}
              </p>
              {!submitted && (
                <div className="flex gap-4">
                  <Button
                    label="نعم"
                    variant="primary-brand"
                    size="lg"
                    onClick={() => {
                      FeedbackLogger.info("نقر على زر نعم");
                      setAnswer({ ...answer, isUseful: "yes", reasons: [] });
                      setOpenQuestions(true);
                    }}
                  />
                  <Button
                    label="لا"
                    variant="primary-brand"
                    size="lg"
                    onClick={() => {
                      FeedbackLogger.info("نقر على زر لا");
                      setAnswer({ ...answer, isUseful: "no", reasons: [] });
                      setOpenQuestions(true);
                    }}
                  />
                </div>
              )}
            </div>
            <div>
              {(!openQuestions || submitted) && stats.totalCount > 0 && (
                <p className="text-sm-regular text-[#161616] text-end">
                  {stats.yesPercentage}% من المستخدمين قالوا نعم من{" "}
                  {stats.totalCount} تعليقًا
                </p>
              )}
              {openQuestions && !submitted && (
                <Button
                  label="إغلاق"
                  variant="subtle"
                  trailIcon
                  trailIconType="cancel-circle"
                  trailIconProps={{ variant: "stroke", type: "standard" }}
                  size="lg"
                  onClick={() => {
                    FeedbackLogger.info("إغلاق نموذج الفيدباك");
                    setOpenQuestions(false);
                  }}
                />
              )}
            </div>
          </div>

          {/* نموذج الفيدباك المفصل */}
          {openQuestions && answer.isUseful && !submitted && (
            <>

              {(errors.gender || errors.reasons) && (

                <DgaNotification
                  content="نرجو منك  استكمال الاستبيان لإرسال التقييم"
                  icon
                  leadText="مهم"
                  link
                  variant="critical"
                />
              )}

              <div className="w-full flex justify-between max-md:flex-col max-md:gap-8">

                <div>
                  <div className="flex flex-col gap-4">
                    <h4 className="text-md-semibold text-[#161616]">
                      يرجى إخبارنا بالسبب
                      <span className="text-sm-regular text-[#6C737F]">
                        &nbsp;
                        (يمكنك تحديد خيارات متعددة)
                      </span>
                    </h4>
                    {options[answer.isUseful] &&
                      options[answer.isUseful].map((option) => (
                        <DgaCheckbox
                          key={option.id}
                          label={option.text}
                          value={option.id}
                          checked={answer.reasons.includes(option.id)}
                          onChange={() => handleReasonChange(option.id)}
                        />
                      ))}
                    {errors.reasons && (

                      <div class="invalid-feedback flex justify-start gap-2 mt-1" translate="yes" lang="ar">
                        <DgaIcon
                          color="#B42318"
                          icon="alert-circle"
                          size={16}
                          type=""
                          variant="stroke"
                          translate="yes" 
                          lang="ar"
                        />
                        يرجى اختيار سبب واحد على الأقل
                      </div>


                    )}
                  </div>
                </div>

                <Textarea
                  ref={textareaRef}
                  label="الملاحظات"
                  name="notes"
                  value={answer.notes}
                  scrollbar
                  resize
                  onChange={(e) => {
                    FeedbackLogger.info("تغيير نص الملاحظات", {
                      value: e.target.value,
                      length: e.target.value.length,
                    });
                  }}
                  variant="default"
                  translate="yes" 
                  lang="ar"
                />
              </div>


              <div className="grid gap-2" >

                <div className="flex md:items-center gap-4 max-md:flex-col">
                  <h4 className="text-md-semibold text-[#161616]">أنا</h4>
                  <DgaRadioButton
                    name="gender"
                    label="ذكر"
                    value="male"
                    checked={answer.gender === "male"}
                    onChange={() => handleGenderChange("male")}
                  />
                  <DgaRadioButton
                    name="gender"
                    label="أنثى"
                    value="female"
                    checked={answer.gender === "female"}
                    onChange={() => handleGenderChange("female")}

                  />

                </div>


                {errors.gender && (

                  <div class="invalid-feedback flex justify-start gap-2">
                    <DgaIcon
                      color="#B42318"
                      icon="alert-circle"
                      size={16}
                      type=""
                      variant="stroke"
                    />
                    يرجى تحديد الجنس
                  </div>

                )}
              </div>




              <div className="w-full flex justify-between md:items-center max-md:flex-col gap-[24px]">


                <div className="flex text-md-regular text-[#161616]gap-[2px] ">
                  <p className="text-md-regular text-[#161616] py-2">
                    لمزيد من المعلومات، يمكنك مراجعة&nbsp;
                  </p>
                  <div className="flex justify-center gap-[2px]">
                    <DgaLink
                      external={true}
                      label="بيان المشاركة الإلكترونية"
                      size="lg"
                      variant="brand"
                      onClick={() => {
                        window.open("https://my.gov.sa/ar/content/e-participation#section-1", "_blank", "noopener,noreferrer");
                      }}
                    />
                    <DgaIcon
                      color="#1b8354"
                      icon="link-square-02"
                      size={16}
                      type=""
                      variant="stroke"
                    />
                  </div>


                  &nbsp;و&nbsp;

                  <div className="flex justify-center gap-[2px]">
                    <DgaLink
                      external={true}
                      label="قواعد الاشتراك"
                      size="lg"
                      target="_blank"
                      onClick={() => {
                        window.open("https://my.gov.sa/ar/content/subscribe#section-1", "_blank", "noopener,noreferrer");
                      }}

                      variant="brand"
                    >
                    </DgaLink>
                    <DgaIcon
                      color="#1b8354"
                      icon="link-square-02"
                      size={16}
                      type=""
                      variant="stroke"
                    />
                  </div>



                </div>


                <Button
                  label="إرسال"
                  size="lg"
                  variant="primary-brand"
                  onClick={handleSubmit}
                />
              </div>
            </>
          )}
        </section>
      </div>
    </>
  );
};

// إضافة دالة لعرض اللوقات في وحدة التحكم
window.showFeedbackLogs = () => {
  const logs = JSON.parse(localStorage.getItem("feedback_logs") || "[]");
  console.table(logs);
  return logs;
};

// إضافة دالة للحصول على آخر الأخطاء
window.getLastFeedbackErrors = () => {
  const logs = JSON.parse(localStorage.getItem("feedback_logs") || "[]");
  return logs.filter((log) => log.level === "error");
};

export default Feedback;
